import { Logout } from '@mui/icons-material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import Close from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuIcon from '@mui/icons-material/Menu';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import {
  AppBar,
  Box,
  Drawer,
  experimentalStyled,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { lighten } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';
import {
  DEBUG_SETTINGS_PATH,
  INSTALLER_TYPES,
  TEAM_SETTINGS_PATH,
  USER_SETTINGS_PATH,
  Z_INDEXES,
} from '../../constants';
import { useLayout } from '../../contexts';
import useAuth from '../../hooks/useAuth';
import noticeError from '../../utils/errors/noticeError';
import AccountPopover from '../account/AccountPopover';
import {
  IS_DEV_OR_STAGING,
  isFeatureEnabled,
} from '../account/userSettings/DebugForm';
import Logo from '../logos/LogoWithName';
import NavRootItem, { NavItem } from '../nav/desktop/NavRootItem';
import ContactInformation from './ContactInformation';

const DashboardNavbarRoot = experimentalStyled(AppBar)(({ theme }) => ({
  ...(theme.palette.mode === 'light' && {
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: 'black',
  }),
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  }),
  zIndex: Z_INDEXES.NAVBAR,
}));

// Recursive function to set the depth of each item
const setDepth = (items: NavItem[], depth = 0) => {
  return items.map((item) => ({
    ...item,
    depth,
    ...(item.items && {
      items: setDepth(item.items, depth + 1),
    }),
  }));
};

const DashboardNavbar = () => {
  const { sidebar } = useLayout();
  const { user, account, logout } = useAuth();
  const theme = useTheme();

  // Custom MediaQuery (this is used as a custom breakpoint for the navbar)
  const isMobileMQ = useMediaQuery('(max-width:1000px)');

  useEffect(() => {
    if (!isMobileMQ && sidebar.isOpen) {
      sidebar.onClose();
    }
  }, [isMobileMQ, sidebar.isOpen]);

  useEffect(() => {
    // Need this for the menu not to be scrollable through the whole heigh of the page
    const rootElement = document.getElementById('root')!;
    if (sidebar.isOpen) {
      rootElement.style.overflow = 'hidden';
    } else {
      rootElement.style.overflow = 'auto';
    }

    return () => {
      rootElement.style.overflow = 'auto';
    };
  }, [sidebar.isOpen]);

  const isInstaller = user?.is_installer;

  const isConsultant = user?.is_consultant;

  const installerOrConsultant = isInstaller || isConsultant;

  const contractorType = account?.contractor_type;

  const navItems: NavItem[] = [
    {
      path: `/dashboard/${
        installerOrConsultant ? 'borrowers' : 'applications'
      }`,
      label: 'Dashboard',
    },
  ];

  if (!installerOrConsultant) {
    navItems.push({ path: '/dashboard/documents', label: 'Documents' });
    navItems.push({ path: '/dashboard/faq', label: 'FAQ' });
  }

  if (installerOrConsultant) {
    navItems.push({
      path: '/dashboard/price-a-project',
      label: 'Calculator',
    });
  }

  if (installerOrConsultant && isFeatureEnabled('showInstallerResources')) {
    navItems.push({
      path: '/dashboard/resources/marketing-materials',
      label: 'Resources',
    });
  }

  if (user?.installer_role === INSTALLER_TYPES.ADMIN) {
    navItems.push({
      path: `/${TEAM_SETTINGS_PATH}`,
      label: 'Admin',
    });
  }

  const handleLogout = async () => {
    try {
      sidebar.onClose();
      await logout();
    } catch (err) {
      noticeError(err);
      toast.error('Unable to logout.');
    }
  };

  const itemsWithDepth = setDepth(navItems);

  const menuItemStyles = { p: 1.5 };
  const listIconStyles = {
    '&.MuiListItemIcon-root': {
      minWidth: 32,
      mr: 0,
      color: 'text.secondary',
    },
  };

  return (
    <>
      <DashboardNavbarRoot>
        <Toolbar>
          <Box
            minHeight={81}
            maxHeight={120}
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            flexWrap="nowrap"
            flexGrow={1}
            sx={{ px: 0.5 }}
          >
            {isMobileMQ ? (
              <IconButton
                color="inherit"
                onClick={sidebar.onToggle}
                size="large"
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            ) : null}

            <Box
              display="flex"
              flexGrow={isMobileMQ ? 1 : 0}
              justifyContent="center"
            >
              <a href={`${__landingUrl__}`}>
                <Logo width="100%" minWidth="187px" maxWidth="187px" />
              </a>
            </Box>

            {!isMobileMQ ? (
              <>
                <Box display="flex" alignSelf="stretch" flexGrow={1}>
                  {itemsWithDepth.map(({ path, label, items }, i) => (
                    <NavRootItem
                      key={i}
                      path={path}
                      label={label}
                      items={items}
                    />
                  ))}
                </Box>

                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gap={2}
                  alignItems="center"
                >
                  <ContactInformation isMobile={false} user={user!} />
                  <AccountPopover />
                </Box>
              </>
            ) : null}
          </Box>
        </Toolbar>
      </DashboardNavbarRoot>

      {/* Mobile Menu */}
      <Drawer
        open={sidebar.isOpen}
        anchor="left"
        onClose={() => sidebar.onClose()}
        sx={{
          '& .MuiDrawer-paper': {
            width: '75vw',
          },
          overflow: 'hidden',
          zIndex: Z_INDEXES.MOBILE_NAVBAR,
        }}
      >
        <Grid
          container
          minHeight={50}
          alignItems="center"
          padding={2}
          borderBottom={`1px solid ${theme.palette.divider}`}
          wrap="nowrap"
        >
          <Grid item>
            <IconButton color="inherit" onClick={sidebar.onToggle} size="large">
              <Close fontSize="medium" />
            </IconButton>
          </Grid>

          <Grid
            item
            xs={8}
            display="flex"
            justifyContent="center"
            margin="0 auto"
          >
            <a href={`${__landingUrl__}`}>
              <Logo width="100%" maxWidth="250px" />
            </a>
          </Grid>
        </Grid>

        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
          height="100vh"
        >
          <Grid
            container
            item
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
            maxWidth="100%"
            maxHeight="fit-content"
          >
            {itemsWithDepth.map(({ path, label, items }, i) => (
              <NavRootItem
                key={i}
                path={path}
                label={label}
                items={items}
                onClose={() => sidebar.onClose()}
              />
            ))}
          </Grid>
          <Grid
            container
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              backgroundColor: lighten(theme.palette.divider, 0.8),
            }}
          >
            <Grid xs={12}>
              <ContactInformation isMobile={true} user={user!} />
            </Grid>

            <Grid
              container
              sx={{
                flexDirection: 'column',
                justifyContent: 'space-between',
                margin: '1rem 0',
              }}
            >
              <MenuItem
                component={RouterLink}
                to={`/${USER_SETTINGS_PATH}`}
                onClick={sidebar.onToggle}
                sx={menuItemStyles}
              >
                <ListItemIcon sx={listIconStyles}>
                  <AccountBoxIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body1" color="textSecondary">
                      My Profile
                    </Typography>
                  }
                />
              </MenuItem>
              {user?.installer_role === INSTALLER_TYPES.ADMIN ? (
                <MenuItem
                  component={RouterLink}
                  to={`/${TEAM_SETTINGS_PATH}`}
                  onClick={sidebar.onToggle}
                  sx={menuItemStyles}
                >
                  <ListItemIcon sx={listIconStyles}>
                    <GroupsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" color="textSecondary">
                        Admin
                      </Typography>
                    }
                  />
                </MenuItem>
              ) : null}
              {IS_DEV_OR_STAGING && (
                <MenuItem
                  component={RouterLink}
                  to={`/${DEBUG_SETTINGS_PATH}/`}
                  sx={menuItemStyles}
                >
                  <ListItemIcon sx={listIconStyles}>
                    <TuneOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" color="textSecondary">
                        Debug
                      </Typography>
                    }
                  />
                </MenuItem>
              )}
              <MenuItem onClick={handleLogout} sx={menuItemStyles}>
                <ListItemIcon sx={listIconStyles}>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      color="textSecondary"
                      variant="body1"
                      textAlign={'start'}
                    >
                      Log out
                    </Typography>
                  }
                />
              </MenuItem>
            </Grid>
          </Grid>
        </Grid>
      </Drawer>
    </>
  );
};

DashboardNavbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func,
};

export default DashboardNavbar;
